import { Injectable } from '@angular/core';
import {
  aggregateCategories,
  VenderProductInfo,
  VenderProductNotifyInfo,
  VenderProductOrderDetailInfo,
  VenderProductOrderInfo,
} from '@mommy/models/vender-product.model';
import { VenderProductService } from '@mommy/services/vender/vender-product.service';
import { UtilService } from '@mommy/services/util.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  CancelVenderProductOrder,
  CreateVenderProductOrder,
  LoadVenderProductDetail,
  LoadVenderProductList,
  LoadVenderProductOrderDetail,
  LoadVenderProductOrderList,
} from './vender-product.actions';

export interface VenderProductStateModel {
  loading: boolean;
  team_id: number | null; // 目前瀏覽的團隊（後端自動解析對應商家）
  products: VenderProductInfo[];
  product_detail: VenderProductInfo | null;
  orders: VenderProductOrderInfo[];
  order_detail: VenderProductOrderDetailInfo | null;
  notifications: VenderProductNotifyInfo[]; // 對應 order_detail 的通知記錄
}

const defaultVenderProductState = (): VenderProductStateModel => ({
  loading: false,
  team_id: null,
  products: [],
  product_detail: null,
  orders: [],
  order_detail: null,
  notifications: [],
});

@State<VenderProductStateModel>({
  name: 'VenderProductState',
  defaults: defaultVenderProductState(),
})
@Injectable()
export class VenderProductState {
  constructor(
    private venderProductService: VenderProductService,
    private util: UtilService
  ) {}

  //#region ======================== Selector ========================
  @Selector()
  static loading(state: VenderProductStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static products(state: VenderProductStateModel): VenderProductInfo[] {
    return state.products;
  }

  /** 從商品清單聚合出去重後的分類（chip 過濾用） */
  @Selector()
  static categories(state: VenderProductStateModel): string[] {
    return aggregateCategories(state.products);
  }

  @Selector()
  static productDetail(state: VenderProductStateModel): VenderProductInfo | null {
    return state.product_detail;
  }

  @Selector()
  static orders(state: VenderProductStateModel): VenderProductOrderInfo[] {
    return state.orders;
  }

  @Selector()
  static orderDetail(state: VenderProductStateModel): VenderProductOrderDetailInfo | null {
    return state.order_detail;
  }

  @Selector()
  static notifications(state: VenderProductStateModel): VenderProductNotifyInfo[] {
    return state.notifications;
  }
  //#endregion

  //#region ======================== Action ========================

  @Action(LoadVenderProductList)
  async loadVenderProductList(
    ctx: StateContext<VenderProductStateModel>,
    { team_id, keyword }: LoadVenderProductList
  ) {
    console.log('[Action] LoadVenderProductList', team_id, keyword);
    const state = ctx.getState();
    // 切換團隊時先清掉前一團隊的商品，避免閃現舊資料
    ctx.patchState({
      loading: true,
      team_id,
      products: state.team_id === team_id ? state.products : [],
    });
    try {
      const products = await this.venderProductService.show_product_list({
        team_id,
        keyword: keyword?.trim() || undefined,
      });
      ctx.patchState({ loading: false, products });
    } catch (error: any) {
      console.error('LoadVenderProductList error', error);
      ctx.patchState({ loading: false });
      this.util.showErrorWithToash(error?.message || '商品載入失敗，請稍後再試');
    }
  }

  @Action(LoadVenderProductDetail)
  async loadVenderProductDetail(
    ctx: StateContext<VenderProductStateModel>,
    { team_id, product_id }: LoadVenderProductDetail
  ) {
    console.log('[Action] LoadVenderProductDetail', team_id, product_id);
    ctx.patchState({ loading: true, product_detail: null });
    try {
      const product_detail = await this.venderProductService.show_product_detail({
        team_id,
        product_id,
      });
      ctx.patchState({ loading: false, product_detail });
    } catch (error: any) {
      console.error('LoadVenderProductDetail error', error);
      ctx.patchState({ loading: false });
      this.util.showErrorWithToash(error?.message || '商品載入失敗，請稍後再試');
    }
  }

  @Action(CreateVenderProductOrder)
  async createVenderProductOrder(
    ctx: StateContext<VenderProductStateModel>,
    { input }: CreateVenderProductOrder
  ) {
    console.log('[Action] CreateVenderProductOrder', input);
    try {
      await this.venderProductService.create_order(input);
      // 成功後重抓訂單列表，讓「我的登記單」立即看到新單
      await ctx.dispatch(new LoadVenderProductOrderList()).toPromise();
    } catch (error: any) {
      console.error('CreateVenderProductOrder error', error);
      this.util.showErrorWithToash(error?.message || '登記失敗，請稍後再試');
      throw error; // rethrow：頁面據此決定不導頁
    }
  }

  @Action(LoadVenderProductOrderList)
  async loadVenderProductOrderList(
    ctx: StateContext<VenderProductStateModel>,
    { team_id, order_status }: LoadVenderProductOrderList
  ) {
    console.log('[Action] LoadVenderProductOrderList', team_id, order_status);
    ctx.patchState({ loading: true });
    try {
      const orders = await this.venderProductService.show_order_list({
        team_id,
        order_status,
      });
      ctx.patchState({ loading: false, orders });
    } catch (error: any) {
      console.error('LoadVenderProductOrderList error', error);
      ctx.patchState({ loading: false });
      this.util.showErrorWithToash(error?.message || '訂單載入失敗，請稍後再試');
    }
  }

  @Action(LoadVenderProductOrderDetail)
  async loadVenderProductOrderDetail(
    ctx: StateContext<VenderProductStateModel>,
    { order_id }: LoadVenderProductOrderDetail
  ) {
    console.log('[Action] LoadVenderProductOrderDetail', order_id);
    ctx.patchState({ loading: true, order_detail: null, notifications: [] });
    try {
      // 訂單詳情與通知記錄並行載入；通知失敗不阻擋詳情顯示
      const [order_detail, notifications] = await Promise.all([
        this.venderProductService.show_order_detail({ order_id }),
        this.venderProductService
          .show_notification_list({ order_id })
          .catch((error) => {
            console.error('show_notification_list error', error);
            return [] as VenderProductNotifyInfo[];
          }),
      ]);
      ctx.patchState({ loading: false, order_detail, notifications });
    } catch (error: any) {
      console.error('LoadVenderProductOrderDetail error', error);
      ctx.patchState({ loading: false });
      this.util.showErrorWithToash(error?.message || '訂單載入失敗，請稍後再試');
    }
  }

  @Action(CancelVenderProductOrder)
  async cancelVenderProductOrder(
    ctx: StateContext<VenderProductStateModel>,
    { order_id, cancel_remark }: CancelVenderProductOrder
  ) {
    console.log('[Action] CancelVenderProductOrder', order_id);
    try {
      await this.venderProductService.cancel_order({ order_id, cancel_remark });
      // 成功後重抓列表；若正在看該筆詳情也一併更新
      const tasks: Promise<any>[] = [
        ctx.dispatch(new LoadVenderProductOrderList()).toPromise(),
      ];
      if (ctx.getState().order_detail?.order?.order_id === order_id) {
        tasks.push(ctx.dispatch(new LoadVenderProductOrderDetail(order_id)).toPromise());
      }
      await Promise.all(tasks);
    } catch (error: any) {
      console.error('CancelVenderProductOrder error', error);
      this.util.showErrorWithToash(error?.message || '取消失敗，請稍後再試');
      // 狀態可能已被商家變更，重抓讓畫面同步
      ctx.dispatch(new LoadVenderProductOrderList());
      throw error;
    }
  }
  //#endregion
}
